import type { SxProps, Theme } from '@mui/material/styles'

/**
 * Читаемый ответ ученика в режиме проверки.
 *
 * Ответ рисуют те же рендереры, что и решение, только с `readOnly` — а он у
 * всех восемнадцати типов означает одно и то же: MUI-контролы внутри
 * выключаются через `disabled`. Выключенное MUI красит
 * `-webkit-text-fill-color` и полупрозрачностью, и написанное учеником
 * выглядело бледнее подсказки: учитель читал ответ как placeholder и считал,
 * что ученик не ответил вовсе.
 *
 * Возвращаем обычный цвет и непрозрачность, а «править нельзя» оставляем за
 * подложкой и курсором. Правило одно на всю карточку, а не свой вид ответа на
 * каждый тип: рендереры разные, но выключаются одинаково — значит и лечится
 * это в одном месте. Тот же приём применён в просмотре задания
 * (`ExerciseFormDialog`).
 */
export const readableStudentAnswerSx: SxProps<Theme> = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'unset',
    color: 'text.primary',
    cursor: 'default',
  },
  '& .MuiInputBase-root.Mui-disabled': { bgcolor: 'action.hover' },
  // Рамка выключенного поля почти сливается с фоном — без неё непонятно, где
  // кончается ответ на несколько абзацев
  '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiFormLabel-root.Mui-disabled': { color: 'text.secondary' },
  '& .MuiFormHelperText-root.Mui-disabled': { color: 'text.secondary' },
  // Чипы — это ответ у половины типов: собранное предложение, разложенные по
  // группам слова, найденное слово в филворде, отмеченная ошибка
  '& .MuiChip-root.Mui-disabled': { opacity: 1 },
  '& .MuiChip-root.Mui-disabled .MuiChip-label': { color: 'text.primary' },
  // «Верно/неверно» — переключатели: без этого выбранный вариант не отличить
  '& .MuiToggleButton-root.Mui-disabled': { color: 'text.primary', opacity: 1 },
  '& .MuiToggleButton-root.Mui-selected.Mui-disabled': { color: 'primary.main' },
  '& .MuiFormControlLabel-label.Mui-disabled': { color: 'text.primary' },
  '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled': { color: 'text.secondary' },
  // Отмеченный вариант обязан читаться отмеченным: серая галка рядом с серым
  // текстом ничем не отличается от пустого кружка
  '& .MuiCheckbox-root.Mui-checked.Mui-disabled, & .MuiRadio-root.Mui-checked.Mui-disabled': {
    color: 'primary.main',
  },
}
